import type { RewardConfig, RewardXpSourceView } from '@contracts';
import { RewardIcon } from '@ui/shared/icons/RewardIcon';
import { XP_SOURCE_COPY } from '@ui/shared/rewards/xpSourceCopy';

/**
 * `XPSourceRow` (settings-rewards.md §11.2, §11.9) — replaces
 * `XPSourcesTable` on phone. A ruled row reusing `today-view.md` §1.3's
 * `RuledCheckRow` anatomy (top-border-only, last row also gets a bottom
 * border) with a value slot instead of a checkbox: icon tile + title +
 * trailing XP value on row 1, the full "when it pays" sentence allowed to
 * wrap across as many lines as it needs on row 2 — this is the actual fix
 * for the reported bug (the sentence no longer fights for a fixed table
 * column's width).
 */
export function XPSourceRow({
  row,
  editing,
  config,
  isLast,
  onChange,
}: {
  readonly row: RewardXpSourceView;
  readonly editing: boolean;
  readonly config: RewardConfig;
  readonly isLast: boolean;
  readonly onChange: (next: RewardConfig) => void;
}) {
  const copy = XP_SOURCE_COPY[row.source];

  return (
    <div
      role="listitem"
      aria-label={`${copy.label}, ${copy.whenItPays} ${row.value} XP${row.active ? '' : ', not yet active'}`}
      style={{
        padding: 'var(--space-3) 0',
        borderTop: '1px solid var(--border-subtle)',
        borderBottom: isLast ? '1px solid var(--border-subtle)' : 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <span
          aria-hidden="true"
          style={{
            width: '28px',
            height: '28px',
            borderRadius: 'var(--radius-sm)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--surface-hover)',
            flex: 'none',
          }}
        >
          <RewardIcon iconId={copy.iconId} size={16} color="var(--text-secondary)" />
        </span>
        <span style={{ flex: 1, fontSize: 'var(--type-body-size)', fontWeight: 'var(--type-body-weight-emphasis)', color: 'var(--text-primary)' }}>
          {copy.label}
        </span>
        {editing ? (
          <input
            type="number"
            className="num"
            min={0}
            max={999}
            value={row.value}
            onChange={(e) =>
              onChange({
                ...config,
                xp: { ...config.xp, [row.source]: Math.max(0, Math.min(999, Number(e.target.value) || 0)) },
              })
            }
            style={{
              width: '72px',
              minHeight: 'var(--touch-target-min)',
              padding: '0 var(--space-2)',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border-subtle)',
              background: 'var(--surface-sunken)',
              color: 'var(--text-primary)',
              fontSize: 'var(--type-body-size)',
              fontFamily: 'var(--font-family)',
              textAlign: 'right',
              flex: 'none',
            }}
          />
        ) : (
          <span className="num" style={{ fontSize: 'var(--type-body-size)', fontWeight: 'var(--type-body-weight-emphasis)', color: 'var(--text-primary)', flex: 'none' }}>
            {row.value}
          </span>
        )}
      </div>
      <div style={{ paddingLeft: 'calc(28px + var(--space-3))', marginTop: 'var(--space-1)' }}>
        <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>{copy.whenItPays}</span>
        {!row.active && (
          <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginTop: 'var(--space-1)' }}>
            Not yet active — arriving with habits.
          </div>
        )}
      </div>
    </div>
  );
}
